import React, { PureComponent } from 'react';
import { Provider } from 'mobx-react';
import { configureScope, Scope } from '@sentry/browser';
import { init as initAnalytics, track, TrackedEvent } from './core/analytics';
import { SessionStorageKeys } from './core/session-storage';
import { getAccessToken } from './core/authentication';
import { UserStore } from './core/user-store';
import { UiStore } from './core/ui-store';
import AppRouter from './DevPortalRouter';
import { PAGES_BASE_PATHNAMES, WINDOW_RESIZE_TRACKING_DEBOUNCE_MS } from './core/consts';
import { debounce } from 'lodash';
import './core/i18n/i18n';

interface Props {}

export default class App extends PureComponent<Props> {
  uiStore: UiStore;
  userStore: UserStore;

  constructor(props: Props) {
    super(props);

    this.uiStore = new UiStore();
    this.userStore = new UserStore();
  }

  windowOnlineHandler = () => {
    this.uiStore.setIsOnline(true);
    track(TrackedEvent.appUserOnline);
  };

  windowOfflineHandler = () => {
    this.uiStore.setIsOnline(false);
    track(TrackedEvent.appUserOffline);
  };

  trackWindowDimensions = (e) => {
    const { windowDimensions } = this.uiStore;
    if (!e || !windowDimensions) {
      return;
    }
    const { width, height } = windowDimensions;
    track(TrackedEvent.windowResize, { width, height });
  };

  debouncedTrackWindowDimensions = debounce(this.trackWindowDimensions, WINDOW_RESIZE_TRACKING_DEBOUNCE_MS);

  updateWindowDimensions = (e?) => {
    this.uiStore.setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    this.debouncedTrackWindowDimensions(e);
  };

  async componentDidMount() {
    this.updateWindowDimensions();

    const accessToken: string = await getAccessToken();

    if (accessToken) {
      const redirectToUrl: string = sessionStorage.getItem(SessionStorageKeys.POST_LOGIN_REDIRECT);
      sessionStorage.removeItem(SessionStorageKeys.POST_LOGIN_REDIRECT);

      if (
        redirectToUrl &&
        redirectToUrl !== '/' &&
        redirectToUrl !== PAGES_BASE_PATHNAMES.Login &&
        window.location.pathname.length <= 1 &&
        !window.location.search
      ) {
        window.location.replace(redirectToUrl);
        return;
      }
    }

    await this.userStore.updateUserSettings(accessToken);

    initAnalytics(this.userStore.userData).then(() => {
      track(TrackedEvent.dpInitialPageLoad);
    });

    configureScope(async (scope: Scope) => {
      const { userData } = this.userStore;
      if (userData && userData.id) {
        scope.setUser({ id: userData.id });
      }
    });

    window.addEventListener('online', this.windowOnlineHandler);
    window.addEventListener('offline', this.windowOfflineHandler);
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.windowOnlineHandler);
    window.removeEventListener('offline', this.windowOfflineHandler);
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  render() {
    const stores = {
      uiStore: this.uiStore,
      userStore: this.userStore,
    };

    return (
      <Provider {...stores}>
        <AppRouter />
      </Provider>
    );
  }
}
